import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Favorite as FavoriteIcon,
  Hotel as HotelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as bookingService from '../../services/bookingService';
import userService from '../../services/userService';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO';
import { PageHero } from '../../components/ui';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function Profile() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Set initial tab based on hash
  const getInitialTab = () => {
    if (window.location.hash === '#bookings') return 1;
    if (window.location.hash === '#favorites') return 2;
    return 0; // Default to personal info
  };
  const [tabValue, setTabValue] = useState(getInitialTab());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [bookingHistory, setBookingHistory] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);

  // User form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    const fetchUserBookings = async () => {
      setLoadingBookings(true);
      try {
        const response = await bookingService.fetchAllBookings();
        setBookingHistory(response.data || response || []);
      } catch (err) {
        console.error("Failed to fetch bookings:", err);
      } finally {
        setLoadingBookings(false);
      }
    };
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        address: user.address || ''
      });
      fetchUserBookings();
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Listen for hash changes
  useEffect(() => {
    if (location.hash === '#bookings') {
      setTabValue(1);
    } else if (location.hash === '#favorites') {
      setTabValue(2);
    } else {
      setTabValue(0);
    }
  }, [location.hash]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);

    // Update hash when switching tabs manually
    if (newValue === 1) {
      window.history.replaceState(null, '', '#bookings');
    } else if (newValue === 2) {
      window.history.replaceState(null, '', '#favorites');
    } else {
      window.history.replaceState(null, '', window.location.pathname); // Clear hash
    }
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    if (editMode) {
      // Reset form data if canceling edit
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        address: user.address || ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await userService.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        address: formData.address
      });

      // Attempt to refresh user data globally so it reflects immediately
      if (refreshUser) {
        await refreshUser();
      }

      setEditMode(false);
      setLoading(false);
      setSnackbar({
        open: true,
        message: t('Profile updated successfully'),
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      setSnackbar({
        open: true,
        message: t('Failed to update profile'),
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Actual booking history is managed by state


  // Mock favorites data
  const favorites = [
    {
      id: 1,
      name: 'Ram Mandir',
      type: 'Temple',
      image: '/images/ram-mandir.jpg'
    },
    {
      id: 2,
      name: 'Saket Grand Hotel',
      type: 'Hotel',
      image: '/images/hotel1.jpg'
    }
  ];

  const displayName = user?.firstName
    ? `${user.firstName} ${user.lastName || ''}`.trim()
    : user?.name || user?.email || '';

  return (
    <>
      <SEO title="My Account" canonicalUrl="/profile" noindex />
      <PageHero
        compact
        crumbs={[{ label: t('Home'), to: '/' }, { label: t('My Account') }]}
        title={t('My Account')}
        subtitle={displayName}
      />
      <div className="container booking-wrap">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              src={user?.avatar || ""}
              alt={user?.firstName ? `${user.firstName} ${user.lastName}` : "User"}
              sx={{
                width: 100,
                height: 100,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#FF9800' : '#C62828',
                fontSize: '2.5rem'
              }}
            >
              {user?.firstName ? user.firstName.charAt(0).toUpperCase() : "U"}
            </Avatar>
            <Box sx={{ ml: 3 }}>
              <Typography variant="h5" component="h2">
                {user?.firstName ? `${user.firstName} ${user.lastName}` : t('User')}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {user?.email || t('Email not available')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('Member since')} {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : '2025'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="profile tabs"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: (theme) => theme.palette.mode === 'dark' ? '#FFB74D' : '#EF5350',
                  '&.Mui-selected': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#FF9800' : '#C62828',
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#FF9800' : '#C62828',
                }
              }}
            >
              <Tab
                label={t('Personal Information')}
                icon={<EditIcon />}
                iconPosition="start"
              />
              <Tab
                label={t('Booking History')}
                icon={<HistoryIcon />}
                iconPosition="start"
              />
              <Tab
                label={t('Favorites')}
                icon={<FavoriteIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Personal Information Tab */}
          <TabPanel value={tabValue} index={0}>
            <Paper
              elevation={1}
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: 3,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" component="h3">
                  {t('Personal Information')}
                </Typography>
                <Button
                  variant={editMode ? "outlined" : "contained"}
                  color={editMode ? "secondary" : "primary"}
                  startIcon={editMode ? <SaveIcon /> : <EditIcon />}
                  onClick={editMode ? handleSubmit : handleEditToggle}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : editMode ? (
                    t('Save Changes')
                  ) : (
                    t('Edit Profile')
                  )}
                </Button>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('First Name')}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('Last Name')}
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('Email')}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('Phone Number')}
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('Address')}
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    disabled={!editMode || loading}
                  />
                </Grid>
                {editMode && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        variant="text"
                        onClick={handleEditToggle}
                        sx={{ mr: 2 }}
                        disabled={loading}
                      >
                        {t('Cancel')}
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                      >
                        {loading ? <CircularProgress size={24} /> : t('Save Changes')}
                      </Button>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </TabPanel>

          {/* Booking History Tab */}
          <TabPanel value={tabValue} index={1}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              <Typography variant="h6" component="h3" gutterBottom>
                {t('Booking History')}
              </Typography>

              {loadingBookings ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : bookingHistory.length > 0 ? (
                <Grid container spacing={3}>
                  {bookingHistory.map((booking) => (
                    <Grid item xs={12} key={booking.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(198, 40, 40, 0.2)'
                        }}
                      >
                        <CardContent>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={1}>
                              <HotelIcon
                                sx={{
                                  fontSize: 40,
                                  color: (theme) => theme.palette.mode === 'dark' ? '#FF9800' : '#C62828'
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={7}>
                              <Typography variant="h6" component="div">
                                {booking.hotel?.name || 'Hotel'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t('Check-in')}: {new Date(booking.checkInDate).toLocaleDateString()} | {t('Check-out')}: {new Date(booking.checkOutDate).toLocaleDateString()}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t('Guests')}: {booking.numberOfGuests || 1}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {t('Reference Number')}: {booking.referenceNumber}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={4} sx={{ textAlign: 'right' }}>
                              <Typography
                                variant="subtitle1"
                                component="div"
                                sx={{
                                  color: booking.status === 'confirmed' ? 'success.main' : 'text.primary',
                                  fontWeight: 'bold',
                                  textTransform: 'capitalize'
                                }}
                              >
                                {booking.status}
                              </Typography>
                              <Typography variant="h6" component="div">
                                ₹{booking.totalAmount}
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ mt: 1 }}
                                onClick={() => navigate(`/hotels/${booking.hotel?.id}`)}
                              >
                                {t('View Hotel')}
                              </Button>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('No booking history found')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/hotels')}
                  >
                    {t('Book a Hotel')}
                  </Button>
                </Box>
              )}
            </Paper>
          </TabPanel>

          {/* Favorites Tab */}
          <TabPanel value={tabValue} index={2}>
            <Paper
              elevation={1}
              sx={{
                p: 3,
                borderRadius: 2,
                background: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              <Typography variant="h6" component="h3" gutterBottom>
                {t('Favorites')}
              </Typography>

              {favorites.length > 0 ? (
                <Grid container spacing={3}>
                  {favorites.map((favorite) => (
                    <Grid item xs={12} sm={6} md={4} key={favorite.id}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 2,
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            boxShadow: 6
                          }
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            paddingTop: '56.25%', // 16:9 aspect ratio
                            bgcolor: 'grey.200'
                          }}
                        >
                          <Box
                            component="img"
                            src={favorite.image}
                            alt={favorite.name}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: 'rgba(255,255,255,0.8)',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.9)',
                              }
                            }}
                          >
                            <FavoriteIcon color="error" />
                          </IconButton>
                        </Box>
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" component="div">
                            {favorite.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {favorite.type}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {t('No favorites found')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/')}
                  >
                    {t('Explore Ayodhya')}
                  </Button>
                </Box>
              )}
            </Paper>
          </TabPanel>
        </Box>
      </motion.div>
      </div>
    </>
  );
}

export default Profile;
